<template>
  <div
    class="gallery-pack-premium relative clickable-element cursor-pointer"
    :class="pack.pack_type"
    @click="showBuyPopup = true"
  >
    <header class="flexing font-bold text-48 text-texts-standard-default uppercase">
      {{ $replacePlaceholder($t('gallery.typePacks'), '{type}', $t(`gallery.${pack.pack_type}Pack`)) }}
    </header>

    <main class="flex justify-center relative">
      <div class="gallery-pack-premium-img absolute" />
    </main>

    <footer class="absolute bottom-0 flex items-center flex-col justify-evenly">
      <p class="text-28 text-texts-standard-default">{{ $t('gallery.packContains') }}</p>
      <div
        class="gallery-pack-premium-box relative flexing"
        :class="$isSsm ? 'revert-skew' : 'skew'"
      >
        <section class="flexing w-full" :class="{ 'revert-skew': $isWsm }">
          <p class="text-36 text-texts-standard-important mr-1 font-bold">{{ pack.total_pieces }}</p>
          <app-icon icon-name="puzzle-52" />
        </section>
      </div>

      <div class="gallery-pack-premium-info flexing text-30 text-texts-standard-important">
        {{ $t('premiumGroup.moreInfo') }}
      </div>
    </footer>
  </div>

  <info-popup
    v-if="showBuyPopup"
    :popup-title="$t('premiumGroup.chooseQuantity')"
    @close="showBuyPopup = false"
  >
    <gallery-pack-premium-popup :pack="pack" @close="showBuyPopup = false" />
  </info-popup>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import type { PropType } from 'vue'
import InfoPopup from '@/components/Popup/InfoPopup.vue'
import GalleryPackPremiumPopup from './GalleryPackPremiumPopup.vue'
import type { GalleryPack } from '@/interfaces/gallery/Packs'

interface ComponentData {
  showBuyPopup: boolean
}

export default defineComponent({
  name: 'GalleryPackPremium',

  components: { InfoPopup, GalleryPackPremiumPopup },

  props: {
    pack: {
      type: Object as PropType<GalleryPack>,
      required: true
    },
  },

  data(): ComponentData {
    return {
      showBuyPopup: false,
    }
  }
})
</script>

<style lang="scss" scoped>
$types: ('regular', 'premium', 'jumbo');
.gallery-pack-premium {
  width: 32.813rem;
  height: 39.5rem;
  @each $type in $types {
    &.#{$type} {
      header {
        @include background(
          url('#{$path-gallery}premium/gallery-pack-premium-header-#{$type}.avif'),
          contain
        );
        width: 32.813rem;
        height: 5.938rem;
      }
      main {
        @include background(
          url('#{$path-gallery}premium/gallery-pack-premium-body-#{$type}.avif'),
          contain
        );
        width: 32.813rem;
        height: 33.563rem;
        .gallery-pack-premium-img {
          @include background(
            url('#{$path-gallery}premium/gallery-pack-premium-img-#{$type}.avif'),
            contain
          );
          width: 25.313rem;
          height: 27.125rem;
          top: 0.25rem;
        }
      }
      footer {
        @include background(
          url('#{$path-gallery}premium/gallery-pack-premium-footer-#{$type}.avif'),
          contain
        );
        width: 32.813rem;
        height: 12.5rem;
      }
    }
  }
  &-box {
    width: 16.25rem;
    height: 4rem;
    border-style: solid;
    border-width: 0.125rem;
    border-image-slice: 1;
    @if $isSsm {
      background-color: #363e5d;
      border-image-source: linear-gradient(to top, #fff284, #f6c717);
      box-shadow:
        inset -0.188rem 0 2.5rem 0 rgba(6, 34, 55, 0.48),
        inset -0.0188rem -0.188rem 2.5rem 0 rgba(6, 34, 55, 0.48);
    }
    @if $isWsm {
      background-color: #0f3251;
      border-image-source: linear-gradient(to top, #fff284, #f6c717);
      box-shadow:
        inset -0.188rem 0 2.5rem 0 rgba(51, 123, 214, 0.19),
        inset -0.0188rem -0.188rem 2.5rem 0 rgba(6, 34, 55, 0.48);
    }
  }
  &-info {
    width: 18.813rem;
    height: 3.75rem;
    border-style: solid;
    border-width: 0.125rem;
    border-image-slice: 1;
    background: linear-gradient(to right, transparent, rgba(20, 20, 20, 0.5), transparent);
    @if $isSsm {
      border-image-source: linear-gradient(to right, transparent, #feb942, transparent);
    }
    @if $isWsm {
      border-image-source: linear-gradient(to right, transparent, #fadd1e, transparent);
    }
  }
}
.gallery-pack-buy-popup {
  width: 85.5rem;
  height: 43.625rem;
  @each $type in $types {
    &.#{$type} {
      @include background(url('#{$path-gallery}premium/popup-body-#{$type}.avif'), contain);
      .popup-left-img {
        @include background(
          url('#{$path-gallery}premium/gallery-pack-premium-img-#{$type}.avif'),
          contain
        );
        width: 19.313rem;
        height: 21.125rem;
        top: 0.25rem;
      }
      .popup-left-info {
        top: -1rem;
      }
      .popup-left-box {
        width: 16.25rem;
        height: 4rem;
        border-style: solid;
        border-width: 0.125rem;
        border-image-slice: 1;
        @if $isSsm {
          background-color: #363e5d;
          border-image-source: linear-gradient(to top, #fff284, #f6c717);
          box-shadow:
            inset -0.188rem 0 2.5rem 0 rgba(6, 34, 55, 0.48),
            inset -0.0188rem -0.188rem 2.5rem 0 rgba(6, 34, 55, 0.48);
        }
        @if $isWsm {
          background-color: #0f3251;
          border-image-source: linear-gradient(to top, #fff284, #f6c717);
          box-shadow:
            inset -0.188rem 0 2.5rem 0 rgba(51, 123, 214, 0.19),
            inset -0.0188rem -0.188rem 2.5rem 0 rgba(6, 34, 55, 0.48);
        }
      }
    }
  }
  .popup-buy-box {
    width: 51.875rem;
    height: 7.5rem;
    border-style: solid;
    border-width: 0.125rem;
    border-image-slice: 1;
    border-image-source: linear-gradient(to top, #f6c717, #fff284);
    background-image: linear-gradient(to right, #1d1f2c, #2e3c63, #1d1f2c);
    padding: 0.938rem 1.688rem;
    @if $isWsm {
      border-image-source: #fadd1e;
      background-image: linear-gradient(to right, #18334e, #215d84, #18334e);
    }
    @if $isSsm {
      border-image-source: linear-gradient(to top, #f6c717, #fff284);
      background-image: linear-gradient(to right, #1d1f2c, #2e3c63, #1d1f2c);
    }
    &-old-price {
      position: absolute;
      height: 4rem;
      top: -0.5rem;
      right: 11rem;
      &:before {
        content: '';
        position: absolute;
        background: #b52e2d;
        width: 7.8125rem;
        height: 0.25rem;
        left: 49%;
        transform: translate(-50%) skewY(-12deg);
        top: 45%;
      }
    }
    &-best-value {
      @include background(url('#{$path-gallery}premium/best-value.avif?v1'), contain);
      width: 17.375rem;
      height: 2.875rem;
      padding-left: 1.5rem;
      @if $isWsm {
        padding-left: 2.5rem;
      }
    }
    &-discount {
      width: 8.625rem;
      height: 2.625rem;
      background-image: linear-gradient(to right, #ed1616, #ff3f3f);
      right: -1.75rem;
    }
    &-value {
      position: relative;
      top: -1rem;
      p {
        background-clip: text;
        text-shadow: none;
        color: transparent;

        @if $isWsm {
          background-image: linear-gradient(to top, #bdbdbd 13%, #ffffff 100%);
        }

        @if $isSsm {
          background-image: linear-gradient(to top, #d7d7d7 13%, #ffffff 100%);
        }
      }
    }
    #buy-gallery-pack {
      right: -4rem;
    }
  }
}
</style>
